// UserProfileFeed — Feed tab: social posts + shared creations
import { Heart, MessageCircle, Share2, UserPlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_URL, apiFetch } from '../config/api'
import { useAuth } from '../contexts/AuthContext'

interface PostItem {
  id: number; content: string; post_type: string; created_at: string
  like_count: number; comment_count: number; media_url?: string
}

interface Props { userId: number }

function formatDate(s: string) {
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return s }
}

export default function UserProfileFeed({ userId }: Props) {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [posts, setPosts] = useState<PostItem[]>([])
  const [loading, setLoading] = useState(true)
  const [likedIds, setLikedIds] = useState<Set<number>>(new Set())

  const loadPosts = () => {
    setLoading(true)
    apiFetch(`${API_URL}/api/social/posts?user_id=${userId}&limit=20`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => { setPosts(d?.posts ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }

  useEffect(() => { loadPosts() }, [userId])

  // Listen for share-to-profile events
  useEffect(() => {
    const handler = (e: Event) => {
      const { type, url, title, content } = (e as CustomEvent).detail ?? {}
      apiFetch(`${API_URL}/api/social/posts`, {
        method: 'POST',
        body: JSON.stringify({ content: title || content, media_url: url, post_type: type || 'creation' })
      }).then(() => loadPosts()).catch(() => {})
    }
    window.addEventListener('runai:share-to-profile', handler)
    return () => window.removeEventListener('runai:share-to-profile', handler)
  }, [])

  const handleLike = async (postId: number) => {
    if (!isAuthenticated) return
    const already = likedIds.has(postId)
    setLikedIds(prev => { const s = new Set(prev); already ? s.delete(postId) : s.add(postId); return s })
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, like_count: p.like_count + (already ? -1 : 1) } : p))
    await apiFetch(`${API_URL}/api/social/posts/${postId}/like`, { method: already ? 'DELETE' : 'POST' }).catch(() => {})
  }

  const handleShareToSocial = async (post: PostItem) => {
    if (!isAuthenticated) return
    await apiFetch(`${API_URL}/api/social/posts`, {
      method: 'POST',
      body: JSON.stringify({ content: post.content, media_url: post.media_url, post_type: 'shared' })
    }).catch(() => {})
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading feed...</div>

  if (posts.length === 0) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <UserPlus size={32} style={{ color: 'var(--text-tertiary)', marginBottom: 10 }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>No posts yet</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>Posts and shared creations will appear here</div>
    </div>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {posts.map(post => (
        <div key={post.id} style={{ background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
          {/* Media */}
          {post.media_url && (
            <div style={{ width: '100%', maxHeight: 320, overflow: 'hidden' }}>
              {post.post_type === 'image' || post.media_url.match(/\.(jpg|jpeg|png|gif|webp)/i)
                ? <img src={post.media_url} alt="" style={{ width: '100%', objectFit: 'cover', maxHeight: 320 }} />
                : post.post_type === 'music' || post.media_url.match(/\.(mp3|wav|ogg)/i)
                  ? <audio controls src={post.media_url} style={{ width: '100%', padding: '12px 16px' }} />
                  : null
              }
            </div>
          )}

          <div style={{ padding: '12px 16px' }}>
            {/* Type badge */}
            {post.post_type && post.post_type !== 'text' && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, background: 'rgba(99,102,241,0.12)', color: '#6366f1', marginBottom: 8, display: 'inline-block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{post.post_type}</span>
            )}

            <p style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, margin: '0 0 10px' }}>{post.content}</p>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{formatDate(post.created_at)}</span>
              <div style={{ display: 'flex', gap: 4 }}>
                <PostAction icon={<Heart size={13} fill={likedIds.has(post.id) ? '#ef4444' : 'none'} />} label={String(post.like_count)} onClick={() => handleLike(post.id)} active={likedIds.has(post.id)} color="#ef4444" />
                <PostAction icon={<MessageCircle size={13} />} label={String(post.comment_count)} onClick={() => navigate(`/social/post/${post.id}`)} color="#6366f1" />
                <PostAction icon={<Share2 size={13} />} label="Share" onClick={() => handleShareToSocial(post)} color="#10b981" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function PostAction({ icon, label, onClick, active, color }: { icon: React.ReactNode; label: string; onClick: () => void; active?: boolean; color: string }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 7, border: 'none', background: active ? `${color}18` : 'transparent', color: active ? color : 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.color = color }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? `${color}18` : 'transparent'; e.currentTarget.style.color = active ? color : 'var(--text-secondary)' }}>
      {icon}{label}
    </button>
  )
}
